import { Duration } from 'luxon';
import { CreateClaimForm } from 'interfaces';
import type { DateTimeInput } from 'utils/dateUtils';
import { nowISO, parseDateTime, toISODate } from 'utils/dateUtils';

/**
 * Build one claim per day between startDate and endDate (inclusive),
 * preserving the time-of-day and duration from the base claim.
 */
export const buildRepeatedClaims = (
  baseClaim: CreateClaimForm,
  startDate: DateTimeInput,
  endDate: DateTimeInput,
  options?: { excludedDates?: DateTimeInput[] }
): CreateClaimForm[] => {
  const baseStart = parseDateTime(baseClaim.serviceStart);
  const baseEnd = parseDateTime(baseClaim.serviceEnd);
  const startTemplate = baseStart.isValid ? baseStart : parseDateTime(nowISO());
  const endTemplate = baseEnd.isValid
    ? baseEnd
    : startTemplate.plus({ minutes: 15 });
  const duration =
    endTemplate > startTemplate
      ? endTemplate.diff(startTemplate)
      : Duration.fromObject({ minutes: 30 });
  const baseZone = startTemplate.zoneName;

  const normalizeDay = (date: DateTimeInput) =>
    parseDateTime(date)
      .setZone(baseZone, { keepLocalTime: true })
      .startOf('day');

  const startDay = normalizeDay(startDate);
  const endDay = normalizeDay(endDate);
  if (!startDay.isValid || !endDay.isValid || endDay < startDay) {
    return [];
  }

  const excludedDateSet = new Set(
    (options?.excludedDates ?? [])
      .map((date) => {
        if (date instanceof Date) {
          return toISODate(date);
        }
        const parsed = parseDateTime(date);
        return parsed.isValid ? parsed.toISODate() : null;
      })
      .filter((date): date is string => Boolean(date))
  );

  const claims: CreateClaimForm[] = [];
  for (
    let current = startDay;
    current <= endDay;
    current = current.plus({ days: 1 })
  ) {
    if (excludedDateSet.has(current.toISODate())) continue;
    const clone = JSON.parse(JSON.stringify(baseClaim)) as CreateClaimForm;
    const targetStart = startTemplate.set({
      year: current.year,
      month: current.month,
      day: current.day,
    });
    const targetEnd = targetStart.plus(duration);
    clone.serviceStart = targetStart.toISO();
    clone.serviceEnd = targetEnd.toISO();
    clone.createdAt = nowISO();
    claims.push(clone);
  }

  return claims;
};
